import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PaidIcon from "@mui/icons-material/Paid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MemberPortalGuard from "./MemberPortalGuard";
import { getMemberInvoices } from "../../api/memberPortalClient";
import TablePaginationActions from "../../base-components/TablePaginationActions";
import Popup from "../../base-components/Popup";
import {
  PageHeader,
  StatCard,
  EmptyState,
  LoadingSkeleton,
  formatMoney,
  formatDate,
} from "./components/MemberUI";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "paid", label: "Paid" },
  { key: "valid", label: "Valid" },
  { key: "all", label: "All" },
];

function statusColor(status) {
  switch (status) {
    case "Paid":
      return "success";
    case "Pending":
      return "warning";
    case "Partial":
      return "info";
    case "Overdue":
      return "error";
    case "Cancelled":
      return "default";
    default:
      return "default";
  }
}

function MemberInvoicesContent() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [summary, setSummary] = useState({
    pending: 0,
    paid: 0,
    all: 0,
  });

  const loadSummary = useCallback(async () => {
    try {
      const [pendingRes, paidRes, allRes] = await Promise.all([
        getMemberInvoices({ status: "pending", page: 1, limit: 1 }),
        getMemberInvoices({ status: "paid", page: 1, limit: 1 }),
        getMemberInvoices({ status: "all", page: 1, limit: 1 }),
      ]);
      setSummary({
        pending: pendingRes.body?.data?.pagination?.total || 0,
        paid: paidRes.body?.data?.pagination?.total || 0,
        all: allRes.body?.data?.pagination?.total || 0,
      });
    } catch {
      // non-fatal
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, body } = await getMemberInvoices({
        status,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (!ok) {
        setError(body?.error?.message || "Failed to load invoices.");
        setPopupOpen(true);
        setInvoices([]);
        setTotal(0);
        return;
      }
      setInvoices(body.data.invoices || []);
      setTotal(body.data.pagination?.total || 0);
    } catch (err) {
      setError(err.message || "Failed to load invoices.");
      setPopupOpen(true);
    } finally {
      setLoading(false);
    }
  }, [status, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const openInvoice = (inv) => {
    const id = inv.invoice_id || inv._id;
    if (id) navigate(`/member/invoices/${id}`);
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      <PageHeader
        title="My Invoices"
        subtitle="View, print, and pay eligible invoices online."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Pending"
          value={summary.pending}
          icon={<PendingActionsIcon />}
          accent="#F7931E"
          onClick={() => {
            setStatus("pending");
            setPage(0);
          }}
        />
        <StatCard
          label="Paid"
          value={summary.paid}
          icon={<PaidIcon />}
          accent="#059669"
          onClick={() => {
            setStatus("paid");
            setPage(0);
          }}
        />
        <StatCard
          label="All invoices"
          value={summary.all}
          icon={<ReceiptLongIcon />}
          accent="#033b51"
          onClick={() => {
            setStatus("all");
            setPage(0);
          }}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              setStatus(tab.key);
              setPage(0);
            }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition ${
              status === tab.key
                ? "bg-gx-red text-white border-gx-red shadow-gx"
                : "bg-gx-surface text-gx-ink border-gx-border hover:border-gx-red"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-gx-surface rounded-gx shadow-gx border border-gx-border overflow-hidden">
        {loading ? (
          <LoadingSkeleton variant="table" />
        ) : invoices.length === 0 ? (
          <EmptyState
            title="No invoices found"
            description={`There are no ${status === "all" ? "" : status + " "}invoices to show.`}
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Pending</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">View</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow
                      hover
                      key={inv.invoice_id || inv._id}
                      className="cursor-pointer"
                      onClick={() => openInvoice(inv)}
                    >
                      <TableCell className="font-medium">
                        {inv.invoice_number || "—"}
                      </TableCell>
                      <TableCell>{formatDate(inv.date)}</TableCell>
                      <TableCell>{formatDate(inv.due_date)}</TableCell>
                      <TableCell>{inv.invoice_type || "—"}</TableCell>
                      <TableCell>{formatMoney(inv.amount)}</TableCell>
                      <TableCell>{formatMoney(inv.pending_amount)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={inv.status || "—"}
                          color={statusColor(inv.status)}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View & print">
                          <IconButton
                            size="small"
                            onClick={() => openInvoice(inv)}
                            sx={{ color: "#D71920" }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      colSpan={8}
                      count={total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(_e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {invoices.map((inv) => (
                <button
                  key={inv.invoice_id || inv._id}
                  type="button"
                  onClick={() => openInvoice(inv)}
                  className="w-full text-left p-4 hover:bg-gx-canvas transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gx-ink">
                        #{inv.invoice_number || "—"}
                      </p>
                      <p className="text-xs text-gx-ink-soft mt-0.5">
                        {inv.invoice_type || "Invoice"} · {formatDate(inv.date)}
                      </p>
                    </div>
                    <Chip
                      size="small"
                      label={inv.status || "—"}
                      color={statusColor(inv.status)}
                    />
                  </div>
                  <div className="mt-3 flex justify-between text-sm">
                    <span className="text-gx-ink-soft">
                      Due {formatDate(inv.due_date)}
                    </span>
                    <span className="font-bold text-gx-ink">
                      {formatMoney(inv.amount)}
                    </span>
                  </div>
                  {Number(inv.pending_amount) > 0 ? (
                    <p className="text-xs text-[#F7931E] mt-1 font-medium">
                      Pending {formatMoney(inv.pending_amount)}
                    </p>
                  ) : null}
                </button>
              ))}
              <div className="px-2">
                <Table>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        colSpan={1}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </div>
          </>
        )}
      </div>

      {popupOpen && (
        <Popup
          alertType="error"
          message={error}
          onClose={() => setPopupOpen(false)}
        />
      )}
    </div>
  );
}

function MemberInvoices() {
  return (
    <MemberPortalGuard>
      <MemberInvoicesContent />
    </MemberPortalGuard>
  );
}

export default MemberInvoices;
